import {
  FilterTree,
  getFieldValueFromItem,
} from "./components/SmartTableFilter/SmartTableFilter";
import {
  IDisabledFn,
  ITableField,
  ITableFieldInlineEdit,
  ITableFieldInlineEditPadded,
  ITableFieldInlineEditWithUnits,
  ITableFieldInlineRadioButtons,
  ITableFieldInlineToggle,
  ITableFieldsByName,
} from "./types/IFields";
import {
  INodes,
  IRow,
  ISortByField,
  ITable,
  ITableSettings,
  TSelectRowProps,
} from "./types/ITable";
import { SelectShoelace, createSelectShoelace } from "@tedivo/tedivo-form";
import {
  calculateTextWidth,
  findParentBy,
  removeChildren,
} from "@tedivo/tedivo-dom-helpers";
import {
  createDictionary,
  generateRandomKey,
} from "@tedivo/tedivo-pure-helpers";

import { IntegratedDialog } from "@tedivo/tedivo-ui";
import SlCheckbox from "@shoelace-style/shoelace/dist/components/checkbox/checkbox";
import SlIcon from "@shoelace-style/shoelace/dist/components/icon/icon";
import SlInput from "@shoelace-style/shoelace/dist/components/input/input";
import SlSwitch from "@shoelace-style/shoelace/dist/components/switch/switch";
import { SmartTableFilterDialog } from "./components/SmartTableFilter/SmartTableFilterDialog";
import { buildObserverWithCallback } from "./helpers/buildObserverWithCallback";
import { sortByFields } from "./helpers/sortByFields";

const DEFAULT_ROWS_VISIBLE = 40;

/** Web component to create a Table */
export class SmartTable<T> extends HTMLElement {
  static numberFormat = Intl.NumberFormat("en-US");

  options: ITable<T>;
  public nodes: INodes;

  rows: Array<IRow<T>>;
  rowsByPk: { [key: string]: IRow<T> } = {};
  selection: Array<IRow<T>> = [];

  private sortedBy: Array<ISortByField<T>> | undefined = undefined;
  public filterText = "";
  private _filterTree: FilterTree<T>;

  /** The data sorted  */
  public sortedData: Array<T>;
  /** The data sorted and filtered */
  public effectiveData: Array<T>;

  private fieldsByName: ITableFieldsByName<T>;
  private _columnsVisible: Array<keyof T & string> = [];
  private _tableWidths: { minWidth: number; percentage: number }[] = [];

  private _searchInput: HTMLInputElement | SlInput | undefined;
  private _searchResults: HTMLSpanElement | undefined;

  private _headerPseudoSlot: HTMLElement | undefined;
  private _headerPseudoSlotContent: HTMLElement | undefined;

  private _rendered = false;
  private _onlyThesePks: string[] = [];

  private _tBodyVisible = true;

  private _dialog: IntegratedDialog<"ok"> | undefined = undefined;
  private _filterDialog: SmartTableFilterDialog<T> | undefined = undefined;

  private _pksSelected = new Set<string>();
  private _rowsCheckboxes: SlCheckbox[] = [];
  private _globalCheckbox: SlCheckbox | undefined = undefined;
  private _globalSelectedActionButton: SelectShoelace | undefined;

  private _tdFilter: HTMLTableCellElement | undefined = undefined;

  constructor() {
    super();
    this.sortedData = [];
    this.effectiveData = [];
    this.fieldsByName = {} as ITableFieldsByName<T>;
    this.rows = [];
    this.options = {} as ITable<T>;
    this.nodes = {} as INodes;
    this._filterTree = {} as FilterTree<T>;
  }

  /**
   * Creates a sortable/filterable table
   * @example {
   *  className: "extra",
   *  tableNode: HTMLTableElement,
   *  title: "Table title",
   *  fields: Array<IField<T>>,
   *  defaultSort: "containerID",
   *  data: Array<T>,
   *  settings: {
   *    filters: { textFieldEnabled: true } | false, // allow filtering
   *    sortEnabled: true | false, // allow column sorting
   *    draggable: true | false, // allow selection by dragging
   *    autoSelectOnClick: true | false, // auto select row on click
   *    preserveWidthOnUpdate: true | false, // keep column widths after updates
   *    sortByLabel: "Sort by", // label
   *    searchLabel: "Filter by", // label
   *  },
   *  pkFunction: (dta: T) => string;
   *  onSortFunction?: (key: keyof T, ascending: boolean) => void;
   *  onClick?: (pk: string, index: number, target: HTMLElement) => void;
   * }
   */
  initialize(options: ITable<T>) {
    const resolvedOptions = options;
    this.options = resolvedOptions;

    let data: Array<T>;

    this.fieldsByName = resolvedOptions.fields.reduce((fbn, f) => {
      if (f) fbn.set(f.name, f);
      if (f && !f?.internalId) f.internalId = generateRandomKey();
      return fbn;
    }, new Map<keyof T, ITableField<T>>());

    const resolvedSort =
      resolvedOptions.settings?.userOptionsStorage?.retrieveSortFields?.() ||
      resolvedOptions.defaultSortBy;

    if (resolvedSort) {
      const arraySort = (
        Array.isArray(resolvedSort) ? resolvedSort : [resolvedSort]
      ) as Array<(keyof T & string) | ISortByField<T>>;

      this.sortedBy = arraySort.reduce((acc, curr) => {
        if (!curr) return acc;

        if (typeof curr === "string") {
          const fieldProps = this.fieldsByName.get(curr);

          acc.push({
            name: curr,
            ascending: true,
            isNumeric:
              fieldProps?.type === "number" ||
              (fieldProps as ITableFieldInlineEdit<T>)?.editType === "number" ||
              (fieldProps as ITableFieldInlineEditPadded<T>)?.type ===
                "inlineEditPadded" ||
              (fieldProps as ITableFieldInlineRadioButtons<T>)?.isNumeric ||
              false,
            sortValueFunction: fieldProps?.sortValueFunction,
          });
        } else {
          const fieldProps = this.fieldsByName.get(curr.name);

          if (fieldProps) {
            acc.push({
              name: curr.name,
              ascending: curr.ascending ?? true,
              isNumeric:
                fieldProps.type === "number" ||
                (fieldProps as ITableFieldInlineEdit<T>)?.editType ===
                  "number" ||
                (fieldProps as ITableFieldInlineEditPadded<T>)?.type ===
                  "inlineEditPadded" ||
                (fieldProps as ITableFieldInlineRadioButtons<T>)?.isNumeric ||
                false,
              sortValueFunction:
                curr.sortValueFunction || fieldProps?.sortValueFunction,
            });
          }
        }

        return acc;
      }, [] as Array<ISortByField<T>>);

      data = resolvedOptions.data.slice().sort(sortByFields(this.sortedBy));
    } else {
      data = resolvedOptions.data.slice();
    }

    this.sortedData = data;
    this.effectiveData = data;

    this.rows = new Array(data.length);
    this.rowsByPk = {};

    if (resolvedOptions.settings?.filters?.initialTextFieldFilter) {
      this.filterText = resolvedOptions.settings.filters.initialTextFieldFilter;
      resolvedOptions.settings.filters.initialTextFieldFilter = undefined;
    }

    this._filterTree = new FilterTree<T>({
      labels: this.options.settings.filters?.advancedLabels,
      filterTree:
        resolvedOptions.settings?.filters?.initialTreeFilter || undefined,
      fieldsByKey: this.fieldsByName,
    });

    return this;
  }

  connectedCallback() {
    // Dialog for column selector, needed for buildHtml
    if (this.options.settings.columnSelector) {
      this._dialog = new IntegratedDialog<"ok">({
        parentNode: this,
        buttonsAttrs: [
          {
            value: "ok",
            label: this.options.settings.columnSelectorButtonText,
            variant: "primary",
            type: "submit",
          },
        ],
      });
    }

    this.nodes = this._buildHtml(this.options, false);
    this.attachEvents();
    this.appendChild(this.nodes.table);

    this._rendered = true;

    if (this.options.settings?.filters?.advancedEnabled) {
      // Create Advanced filter dialog
      this._filterDialog = new SmartTableFilterDialog({
        options: {
          fields: this.fieldsByName,
          enableStoring: this.options.settings.filters.advancedEnableStoring,
          userOptionsStorage: this.options.settings.userOptionsStorage,
          showPrettyHtml:
            !!this.options.settings.filters.advancedDialogPrettyHtml,
        },
        data: this.sortedData,
        onSave: (newFilterValues) => {
          this._filterTree.setFilters(newFilterValues);
          this.updateData();
        },
        labels: this.options.settings.filters?.advancedLabels,
      });

      // Add event to update the filter TD when the filter tree is updated
      this._filterTree.addEventListener("updated", () => {
        if (!this._tdFilter) return;

        removeChildren(this._tdFilter);

        const isEmpty = this._filterTree.isEmptyFilter();
        this.nodes.filterButtonLauncher?.updateButtonState(isEmpty);

        if (!isEmpty) {
          const icon = document.createElement("sl-icon");
          icon.name = "funnel-fill";

          const conditionPrettifiedHtmlElement =
            this._filterTree.toPrettyHtmlElement(
              (field) => this.fieldsByName.get(field)?.label || String(field),
              (value, field) =>
                getFieldValueFromItem(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  { [field]: value } as any,
                  field,
                  this.fieldsByName,
                ) as string,
              {
                inline: true,
              },
            );

          this._tdFilter.appendChild(icon);
          this._tdFilter.appendChild(conditionPrettifiedHtmlElement);
          this._tdFilter.style.display = "table-cell";
        } else {
          this._tdFilter.style.display = "none";
        }
      });

      if (this.nodes.filterButtonLauncher) {
        this.nodes.filterButtonLauncher.openDialog = () => {
          this._filterDialog?.show(this._filterTree);
        };
      }
    }
  }

  get sortEnabled() {
    return this.options.settings.sortEnabled;
  }

  get filterEnabled() {
    return this.options.settings.filters?.textFieldEnabled;
  }

  get rowsNumber() {
    return this.rows.filter(Boolean).length || 0;
  }

  changeSettings(settings: ITableSettings<T>) {
    this.options.settings = Object.assign(this.options.settings, settings);
    this.updateData();
  }

  changeFields(fields: Array<ITableField<T>>) {
    this.rows = new Array(this.options.data.length);
    this.rowsByPk = {};
    this.options.fields = fields;
    this.updateData();
  }

  get tBodyVisible() {
    return this._tBodyVisible;
  }

  set tBodyVisible(v: boolean) {
    this._tBodyVisible = v;
    if (!this.nodes) return;
    if (v) {
      this.nodes.tbody.style.visibility = "";
    } else {
      this.nodes.tbody.style.visibility = "hidden";
    }
  }

  get restrictedToPks() {
    return this._onlyThesePks;
  }

  set restrictedToPks(pks: string[]) {
    this._onlyThesePks = pks;
    if (this._rendered) this.updateData();
  }

  get columnsVisible() {
    return this._columnsVisible;
  }

  set columnsVisible(cols: Array<keyof T & string>) {
    this._columnsVisible = cols;
    if (this._rendered) this.updateData();
  }

  private _getFieldsVisible(options: ITable<T>) {
    const fields = options.fields.filter(
      (f) =>
        Boolean(f) &&
        (this._columnsVisible.length === 0 ||
          this._columnsVisible.some((c) => c === f?.name)),
    ) as Array<ITableField<T>>;

    const fieldsByName = createDictionary<ITableField<T>, keyof T & string>(
      fields,
      (f) => f.name as keyof T & string,
      true,
    );

    return { fields, fieldsByName };
  }

  get pksSelected() {
    return Array.from(this._pksSelected);
  }
  set pksSelected(pks: string[]) {
    if (!this.options.settings.rowAddCheckboxes) return;

    this._pksSelected = new Set(pks);
    this.selectRows({ pks });

    this._rowsCheckboxes.forEach((c) => {
      c.checked = pks.includes(c.value);
    });

    this._rowsCheckboxes[0]?.dispatchEvent(new CustomEvent("sl-change"));
  }

  private _buildHtml(options: ITable<T>, dataWasUpdated: boolean): INodes {
    const { fields, fieldsByName } = this._getFieldsVisible(options);
    const nodes = this.nodes;

    this.updateEffectiveDataWithSearchAndFilters(this.filterText, true); // Hide depending on previous search

    const sortEnabled = options.settings.sortEnabled;
    const sortVisible = options.settings.sortVisible;

    const docFrag = document.createDocumentFragment();

    const table =
      nodes?.table || options.tableNode || document.createElement("table");

    addResizeObserverToTable(table, options.onSizeChange);

    const thead = document.createElement("thead");
    const tbody = nodes?.tbody || document.createElement("tbody");

    const filterButtonLauncher =
      nodes?.filterButtonLauncher ||
      document.createElement("smart-table-filters-button");

    removeChildren(table);

    table.className = `smart-table ${sortEnabled ? "sort-enabled" : ""} ${
      sortVisible ? "sort-visible" : ""
    } ${options.className || ""}`;

    // Table Header
    const trHead = document.createElement("tr");

    // First column for selection?
    if (options.settings.rowAddCheckboxes) {
      const { rowsActionsSelector, rowsCheckboxes, globalCheckbox } =
        addCheckboxesColumn(this._pksSelected, options.settings);

      this._rowsCheckboxes = rowsCheckboxes;
      this._globalCheckbox = globalCheckbox;
      this._globalSelectedActionButton = rowsActionsSelector;
    }

    // Table header cells
    const ths = (fields.filter(Boolean) as Array<ITableField<T>>).map((f) => {
      const th = createThCellInHeader(f, this.sortedBy, this.onSort);
      trHead.appendChild(th);
      return th;
    });

    const numberOfColumns =
      ths.length + (options.settings.rowAddCheckboxes ? 1 : 0);

    // Table column filters
    const trFilter = document.createElement("tr");
    const tdFilter = document.createElement("td");
    tdFilter.className = "td-filter-summary";
    trFilter.appendChild(tdFilter);
    tdFilter.style.display = "none";
    tdFilter.setAttribute("colspan", String(numberOfColumns));
    this._tdFilter = tdFilter;

    // Append header and filter rows to thead
    thead.appendChild(trHead);
    thead.appendChild(trFilter);

    // Table Body
    this.selectRows({ clearSelection: true });
    this.rows = new Array(this.options.data.length);

    const initialRows = options.initialRows || DEFAULT_ROWS_VISIBLE;
    removeChildren(tbody); // This node is kept. Clean it before.
    this.renderGroupFromToIndex(0, initialRows, tbody, dataWasUpdated);

    // Add all to docFrag
    if (
      options.title ||
      options.settings?.filters?.textFieldEnabled ||
      options.settings?.filters?.advancedEnabled ||
      options.settings?.actionBox ||
      options.settings?.columnSelector
    ) {
      const caption = document.createElement("caption");
      const captionContent = document.createElement("div");
      const headerPseudoSlot = document.createElement("div-divider-element");

      captionContent.className = "smart-table-caption-content";

      caption.appendChild(captionContent);

      const h2 = document.createElement("h2");
      h2.innerHTML = options.title || "";
      captionContent.appendChild(h2);

      // Header pseudo slot (at the left of the actions box)
      this._headerPseudoSlot = headerPseudoSlot;
      headerPseudoSlot.className = "header-pseudo-slot";
      headerPseudoSlot.showPrefix = false;

      if (this._headerPseudoSlotContent)
        headerPseudoSlot.appendChild(this._headerPseudoSlotContent);

      captionContent.appendChild(headerPseudoSlot);

      // Column selector button
      if (options.settings?.columnSelector) {
        const btnChangeColumns = createRowColumnSelector(
          this._dialog,
          this._columnsVisible,
          (cv) => {
            this.columnsVisible = cv;
            options.settings.onColumnSelectorChange?.(cv);
          },
        );

        const firstActions = document.createElement("div-divider-element");
        firstActions.style.order = "1";
        firstActions.appendChild(btnChangeColumns);
        firstActions.showPrefix = false;

        captionContent.appendChild(firstActions);
      }

      const searchBoxHolder = document.createElement("div-divider-element");
      searchBoxHolder.className = "smart-table-search";
      searchBoxHolder.showPrefix = false;

      captionContent.appendChild(searchBoxHolder);

      if (options.settings?.filters?.textFieldEnabled) {
        const searchBox = document.createElement("div");
        const searchLabel = options.settings.labelSearch || "Filter by ...";

        const searchTooltip = document.createElement("sl-tooltip");
        searchTooltip.content =
          searchLabel +
          (this.options.settings.filters?.textFieldFilterFields
            ? ` (${this.options.settings.filters?.textFieldFilterFields
                .filter((n) => !!fieldsByName[n as keyof T & string])
                .map((n) => fieldsByName[n].label)
                .join(", ")})`
            : "");

        searchBoxHolder.appendChild(searchBox);
        searchBox.className = "smart-table-search-box";

        const searchInput = document.createElement("sl-input");
        searchInput.placeholder = searchLabel;
        searchInput.value = this.filterText || "";
        searchInput.clearable = true;
        searchInput.filled = true;
        searchInput.style.width = "auto";
        searchInput.style.textOverflow = "ellipsis";

        const searchResults = document.createElement("span");
        searchResults.className = "smart-table-search-results";
        searchResults.style.display = "none";
        searchBoxHolder.appendChild(searchResults);

        const searchIcon = document.createElement("sl-icon") as SlIcon;
        searchIcon.name = "funnel";
        searchIcon.slot = "prefix";
        searchInput.appendChild(searchIcon);

        searchTooltip.appendChild(searchInput);
        searchBox.appendChild(searchTooltip);

        searchInput.addEventListener("keyup", this.onSearch, false);
        searchInput.addEventListener("paste", this.onSearchPaste, false);
        searchInput.addEventListener("sl-clear", this.clearSearch, false);

        this._searchInput = searchInput;
        // TODO: think of a way to show the number of results
        // this._searchResults = searchResults;
      }

      if (options.settings?.filters?.advancedEnabled) {
        filterButtonLauncher.label =
          this.options.settings.filters?.advancedLabels?.title ??
          "Filter Preview";

        filterButtonLauncher.buttonLabel =
          this.options.settings.filters?.advancedLabels?.buttonLabel ??
          "Adv. Filters";

        searchBoxHolder.appendChild(filterButtonLauncher);
      }

      if (options.settings?.actionBox) {
        captionContent.appendChild(options.settings.actionBox);
      }

      docFrag.appendChild(caption);
    }

    docFrag.appendChild(thead);
    docFrag.appendChild(tbody);

    // Finally append to table
    table.appendChild(docFrag);

    // Set widths of columns
    if (options.settings.preserveWidthOnUpdate)
      calculateInitialColumnWidths({
        tableWidths: this._tableWidths,
        effectiveData: this.effectiveData,
        doSetMinWidth: options.settings.preserveWidthOnUpdate, // Save widths (to prevent re-flow)?
      });

    // Set min-height of parent to prevent re-flow
    this.setParentHeight(table);
    // Counter
    this.updateSearchCounter();

    return {
      table,
      tbody,
      filterButtonLauncher,
    };

    function addResizeObserverToTable(
      table: HTMLTableElement,
      onSizeChange:
        | ((
            tableRect: DOMRect,
            tableHeaderRect: DOMRect,
            tableBodyRec: DOMRect,
          ) => void)
        | undefined,
    ) {
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries.length === 0) return;
        const entry = entries[0];

        const tableRect = entry.target.getBoundingClientRect();
        const tableHeaderRect = thead.getBoundingClientRect();
        const tableBodyRec = tbody.getBoundingClientRect();

        const headerTop = tableHeaderRect.top - tableRect.top;
        const headerBottom = tableHeaderRect.bottom - tableRect.top;

        table.style.setProperty(
          "--data-header-top",
          `${Math.floor(headerTop - 1)}px`,
        );

        table.style.setProperty(
          "--data-header-bottom",
          `${Math.ceil(headerBottom - 41)}px`,
        );

        onSizeChange?.(tableRect, tableHeaderRect, tableBodyRec);
      });

      resizeObserver.observe(table);
    }

    function createRowColumnSelector(
      dialog: IntegratedDialog<"ok"> | undefined,
      columnsVisible: (keyof T & string)[],
      onChangeColumnsVisible:
        | ((columnsVisible: (keyof T & string)[]) => void)
        | undefined,
    ): HTMLElement {
      const btn = document.createElement("sl-button");
      btn.className = "smart-table-column-selector";
      btn.variant = "primary";
      btn.outline = true;

      const icon = document.createElement("sl-icon") as SlIcon;
      icon.name = "layout-three-columns";
      icon.slot = "prefix";

      btn.appendChild(icon);

      const tooltip = document.createElement("sl-tooltip");
      tooltip.content = options.settings.columnSelectorHelp || "Columns";
      tooltip.appendChild(btn);

      btn.addEventListener("click", () => {
        if (!dialog) return;

        const allFields = options.fields.filter(Boolean) as Array<
          ITableField<T>
        >;
        const columnsFixed = options.settings.columnSelectorFixed || [];

        const div = document.createElement("div");
        div.className = "modal-column-selector";

        allFields.forEach((f) => {
          const chkbox = document.createElement("sl-checkbox") as SlCheckbox;
          chkbox.innerHTML = f.label;
          chkbox.value = f.name as string;
          if (columnsFixed.includes(f.name)) {
            chkbox.disabled = true;
            chkbox.checked = true;
          } else {
            chkbox.checked =
              columnsVisible.length === 0 ||
              columnsVisible.includes(f.name as keyof T & string);
          }
          div.appendChild(chkbox);
        });

        dialog.show(options.settings.columnSelectorHelp || "Columns", div);

        dialog.onButtonClicked = (btnClicked) => {
          if (btnClicked === "ok") {
            const checkboxes = div.querySelectorAll("sl-checkbox");
            const columnsVisible = Array.from(checkboxes)
              .filter((c) => (c as SlCheckbox).checked)
              .map((c) => (c as SlCheckbox).value) as Array<keyof T & string>;

            onChangeColumnsVisible?.(columnsVisible);
          }
        };
      });

      return tooltip;
    }

    function createThCellInHeader(
      {
        shortLabel,
        label,
        name,
        notSortable,
        className: classNamePrev,
        fixedWidth,
        smallPadding,
        createSortIconFn,
      }: ITableField<T>,
      sortedBy: ISortByField<T>[] | undefined,
      onSort: (ev: MouseEvent) => void,
    ): HTMLTableCellElement {
      const th = document.createElement("th");

      const className = classNamePrev
        ? typeof classNamePrev === "function"
          ? classNamePrev()
          : classNamePrev
        : "";

      th.className = `${className || ""}`;

      if (fixedWidth) th.style.width = fixedWidth;

      if (smallPadding) th.classList.add("small-padding");

      // Sort buttons
      if ((sortEnabled || sortVisible) && !notSortable) {
        const btnSort = document.createElement("button");

        // Set className
        if (sortedBy) {
          const fieldIsSortedIndex = sortedBy.findIndex((s) => s.name === name);

          if (fieldIsSortedIndex >= 0) {
            const fieldSorted = sortedBy[fieldIsSortedIndex];
            btnSort.className = `sort-by sorted-by-${
              fieldSorted.ascending ? "asc" : "desc"
            } ${fieldIsSortedIndex > 0 ? "sort-by-secondary" : ""}`;
          } else {
            btnSort.className = sortEnabled ? "sort-by" : "no-sort-by";
          }
        } else {
          btnSort.className = sortEnabled ? "sort-by" : "no-sort-by";
        }

        const sortByLabel = options.settings.labelSortBy || "Order by";
        btnSort.innerHTML = shortLabel || label;
        btnSort.dataset["name"] = String(name);
        btnSort.setAttribute("title", `${sortByLabel} ${label}`);

        if (sortedBy && options.settings.createSortIconFn) {
          const effectiveIconFn =
            createSortIconFn || options.settings.createSortIconFn;

          if (effectiveIconFn) {
            const sortIcons = document.createElement("span");
            sortIcons.className = "sort-icons";

            const iconAsc = document.createElement("span");
            iconAsc.className = "icon-asc-holder";
            iconAsc.appendChild(effectiveIconFn(true));

            const iconDesc = document.createElement("span");
            iconDesc.className = "icon-desc-holder";
            iconDesc.appendChild(effectiveIconFn(false));

            sortIcons.appendChild(iconAsc);
            sortIcons.appendChild(iconDesc);
            btnSort.appendChild(sortIcons);
          }
        }

        th.appendChild(btnSort);

        btnSort.addEventListener("click", onSort, false);
      } else {
        th.innerHTML = shortLabel || label;
      }

      th.dataset["name"] = String(name);

      return th;
    }

    function calculateInitialColumnWidths({
      tableWidths,
      effectiveData = [],
      doSetMinWidth = false,
    }: {
      tableWidths: { minWidth: number; percentage: number }[];
      effectiveData?: Array<T>;
      doSetMinWidth?: boolean;
    }) {
      let totalWidth = 0;
      const totalMinWidth = 0;

      if (tableWidths.length === 0) {
        // Headers
        fields.forEach(({ label, shortLabel }, idx) => {
          const w = Math.ceil(calculateTextWidth(shortLabel || label, "15px"));
          tableWidths[idx] = { minWidth: w, percentage: 0 };
        });

        // First rows (10)
        for (let r = 0; r < Math.min(10, effectiveData.length); r += 1) {
          const dta = effectiveData[r];
          fields.forEach(({ name, valueFunction }, idx) => {
            const value = valueFunction
              ? valueFunction(dta)
              : String(dta[name]);
            const w = Math.ceil(calculateTextWidth(value, "15px"));
            if (tableWidths[idx].minWidth < w) {
              tableWidths[idx].minWidth = w;
            }
          });
        }

        // Add up widths
        totalWidth = tableWidths.reduce(
          (prev, curr) => (prev += curr.minWidth),
          0,
        );

        // Calculate percentages
        for (let j = 0; j < tableWidths.length; j += 1) {
          tableWidths[j].percentage =
            Math.round((tableWidths[j].minWidth / totalWidth) * 1000) / 10;
        }
      }

      // Set widths in ths style.minWidth and width in percentage HTML Cell nodes
      if (doSetMinWidth) {
        for (let j = 0; j < ths.length; j += 1) {
          if (tableWidths[j]) {
            ths[j].style.width = `${tableWidths[j].percentage}%`;
            ths[j].style.minWidth = `${tableWidths[j].minWidth}px`;
          }
        }
      }

      return {
        totalWidth,
        totalMinWidth,
      };
    }

    function addCheckboxesColumn(
      pksSelected: Set<string>,
      settings: ITableSettings<T>,
    ): {
      rowsCheckboxes: SlCheckbox[];
      globalCheckbox: SlCheckbox;
      rowsActionsSelector: SelectShoelace<string>;
    } {
      const rowsCheckboxes: SlCheckbox[] = [];
      const th = document.createElement("th");
      th.style.width = "70px";
      th.className = "td-smt-selector z-index-10000";

      const chk = document.createElement("sl-checkbox") as SlCheckbox;
      const globalCheckbox = chk;

      chk.addEventListener("sl-change", (ev) => {
        const target = ev.target as SlCheckbox;
        const checked = target.checked;

        rowsCheckboxes.forEach((c) => {
          c.checked = checked;
          c.dispatchEvent(new CustomEvent("sl-change"));
        });

        if (checked) {
          rowsCheckboxes
            .map((c) => c.value)
            .forEach((v) => {
              pksSelected.add(v);
            });
        } else {
          pksSelected.clear();
        }

        chk.checked = checked;
        chk.indeterminate = false;

        settings.rowOnCheckedChange?.(Array.from(pksSelected));
      });

      const onClickAction = (action: string) => {
        if (options.settings.rowOnClickAction) {
          options.settings.rowOnClickAction(action, Array.from(pksSelected));
        }
      };

      const rowsActionsSelector = createSelectShoelace({
        id: "node-actions",
        options: settings.rowActions || [],
        buttonText: "",
        iconPrefix: "command",
        variant: "primary",
        size: "small",
        textAlignment: "left",
        ommitCheckSign: true,
        placement: "bottom-start",
        onChange: onClickAction,
      });

      rowsActionsSelector.disabled = true;

      th.appendChild(chk);
      th.appendChild(rowsActionsSelector);

      trHead.appendChild(th);

      return {
        rowsCheckboxes,
        globalCheckbox,
        rowsActionsSelector,
      };
    }
  }

  private attachEvents = () => {
    const options = this.options,
      tbody = this.nodes.tbody;

    if (options.settings.draggable) {
      const addDraggability = () => {
        let startIndex = -1;

        const mouseSelectMove = (ev: MouseEvent) => {
          ev.preventDefault();
          const target = ev.target as HTMLElement;
          if (!target) return;
          const tr = findParentBy(target, "TR") as HTMLElement | undefined;
          if (!tr) return;
          const currentIndex = Number(tr.dataset["index"]);
          this.selectRows({ startIndex, endIndex: currentIndex });
        };

        const mouseSelectStart = (ev: MouseEvent) => {
          if (ev.altKey || ev.ctrlKey || ev.shiftKey) return;
          ev.preventDefault();
          const target = ev.target as HTMLElement;
          if (!target) return;
          const tr = findParentBy(target, "TR") as HTMLElement | undefined;
          if (!tr) return;
          startIndex = Number(tr.dataset["index"]);
          tbody.addEventListener("mousemove", mouseSelectMove, false);
        };

        const mouseSelectEnd = (ev: MouseEvent) => {
          if (ev.altKey || ev.ctrlKey || ev.shiftKey) return;
          ev.preventDefault();
          tbody.removeEventListener("mousemove", mouseSelectMove);
          if (options.onSelection)
            options.onSelection(this.selection.map((s) => s.pk));
        };

        tbody.addEventListener("mousedown", mouseSelectStart, false);
        tbody.addEventListener("mouseup", mouseSelectEnd, false);
      };

      addDraggability();
    }

    const optionsOnClickFn = options.onClick;
    if (optionsOnClickFn !== undefined)
      tbody.addEventListener(
        "click",
        (ev) => {
          const target = ev.target as HTMLElement;
          if (!target) return;
          const tr = findParentBy(target, "TR") as HTMLElement | undefined;
          if (!tr) return;
          const pk = tr.dataset["pk"];
          const index = Number(tr.dataset["index"]);
          if (isNaN(index) || !pk) return;
          optionsOnClickFn(pk, index, target);
          if (options.settings.autoSelectOnClick) {
            this.selectRows({ pk });
          }
        },
        false,
      );
  };

  private renderGroupFromToIndex(
    from: number,
    to: number,
    parent: HTMLElement,
    dataWasUpdated: boolean,
  ): void {
    const data = this.effectiveData;
    const dataLenght = data.length;
    const effectiveTo = Math.min(to, dataLenght);

    // Create
    const trs = data
      .filter((_, idx) => idx >= from && idx < effectiveTo)
      .map((dta, index) =>
        this.renderRow(
          dta,
          index + from,
          dataWasUpdated,
          this.options.settings.rowAddCheckboxes || false,
        ),
      );

    // Append
    const docFrag = document.createDocumentFragment();
    trs.forEach((tr) => {
      if (tr !== null) {
        docFrag.appendChild(tr);
      }
    });
    parent.appendChild(docFrag);

    // Add observer to last to auto-load next rows
    const lastTr = trs.length ? trs[trs.length - 1] : undefined;

    if (lastTr) {
      const ioObserver = buildObserverWithCallback((newIndex: number) => {
        this.renderGroupFromToIndex(
          newIndex,
          newIndex + DEFAULT_ROWS_VISIBLE,
          parent,
          dataWasUpdated,
        );
        this.setParentHeight();
      });
      ioObserver.observe(lastTr);
    }
  }

  private renderRow(
    dta: T,
    index: number,
    dataWasUpdated: boolean,
    rowAddCheckboxes: boolean,
  ): HTMLTableRowElement | null {
    const options = this.options;
    if (dta === undefined || dta === null) return null;

    const pk = options.pkFunction(dta);
    const className = this.options.rowClassFunction?.(dta) || undefined;

    if (pk === undefined) return null;

    const rowsByPk = this.rowsByPk,
      rowExists = rowsByPk[pk];

    // If row has been generated already
    if (rowExists && !dataWasUpdated) {
      rowExists.index = index;
      rowExists.row.dataset["index"] = String(index);
      this.rows[index] = rowExists;
      if (className && !rowExists.row.classList.contains(className))
        rowExists.row.classList.add(className);
      return rowExists.row;
    }

    const { fields } = this._getFieldsVisible(options);
    const tr = document.createElement("tr");

    tr.dataset["index"] = String(index);
    if (pk) tr.dataset["pk"] = pk;

    if (className) tr.classList.add(className);

    if (rowAddCheckboxes) {
      createTdWithCheckboxSelector(
        pk,
        tr,
        {
          pksSelected: this._pksSelected,
          globalCheckbox: this._globalCheckbox,
          globalSelectedActionButton: this._globalSelectedActionButton,
          rowsCheckboxes: this._rowsCheckboxes,
        },
        (pksSel: Array<string>) => {
          this.selectRows({ pks: pksSel });
          this.options.settings.rowOnCheckedChange?.(pksSel);
        },
      );
    }

    fields.forEach((field) => renderFieldCell(dta, pk, this, tr, field));

    const iRow: IRow<T> = {
      index,
      pk,
      row: tr,
      data: dta,
      visible: true,
    };

    this.rows[index] = iRow;
    rowsByPk[pk] = iRow;

    return tr;
  }

  private onSort = (ev: MouseEvent) => {
    const target = ev.target as HTMLButtonElement,
      name = target.dataset["name"] as keyof T;

    if (this.sortedBy) {
      const fieldIsSortedIndex = this.sortedBy.findIndex(
        (s) => s.name === name,
      );

      if (fieldIsSortedIndex >= 0) {
        const fieldSorted = this.sortedBy[fieldIsSortedIndex];
        fieldSorted.ascending = !fieldSorted.ascending;
      } else if (this.options.settings.sortEnabled) {
        const fieldProps = this.fieldsByName.get(name);
        this.sortedBy = [
          {
            name,
            ascending: true,
            isNumeric: fieldProps?.type === "number" || false,
            sortValueFunction: fieldProps?.sortValueFunction,
          },
        ];
      }
    } else {
      if (!this.options.settings.sortEnabled) return;
      const fieldProps = this.fieldsByName.get(name);
      this.sortedBy = [
        {
          name,
          ascending: true,
          isNumeric: fieldProps?.type === "number" || false,
          sortValueFunction: fieldProps?.sortValueFunction,
        },
      ];
    }

    if (this.options.onSortFunction) {
      this.options.onSortFunction(this.sortedBy, this);
    } else {
      this.updateData();
    }

    if (this.options.onSortParamsChanged) {
      this.options.onSortParamsChanged(this.sortedBy);
    }

    !this.nodes.table.classList.contains("just-sorted") &&
      this.nodes.table.classList.add("just-sorted");
    window.setTimeout(() => {
      this.nodes.table.classList.remove("just-sorted");
    }, 500);
  };

  private onSearchPaste = (ev: ClipboardEvent) => {
    if (!ev.clipboardData) return;
    const text = ev.clipboardData.getData("text");
    this.applySearch(text.toUpperCase());
  };

  private onSearch = (ev: KeyboardEvent) => {
    const target = ev.target as HTMLInputElement,
      value = target.value.toUpperCase();

    if (ev.code === "Escape") {
      this.clearSearch();
    } else {
      this.applySearch(value);
    }
  };

  private setParentHeight = (tablePassed?: HTMLTableElement) => {
    const settings = this.options.settings,
      table = tablePassed || this.nodes.table,
      parentTable = table.parentElement;

    if (!settings.fixHeightOfParentElement || !parentTable) {
      return;
    }

    setTimeout(() => {
      const bbox = table.getBoundingClientRect();
      parentTable.style.minHeight = `${Math.ceil(bbox.height)}px`;
    }, 50);
  };

  /**
   * Updates the effectiveData
   * @param searchTerm
   */
  public updateEffectiveDataWithSearchAndFilters(
    searchTerm: string,
    force = false,
  ): boolean {
    const normalizedSearchTerm = searchTerm.toUpperCase().trim();

    if (!force && normalizedSearchTerm === this.filterText) {
      return false;
    }

    this.filterText = normalizedSearchTerm;
    const pkFunction = this.options.pkFunction;
    const options = this.options;

    const filterTexts = normalizedSearchTerm
      .split(" ")
      .filter(Boolean)
      .map((s) => s.trim());

    const data = this.sortedData;

    const fields = (
      options.fields.filter(Boolean) as Array<ITableField<T>>
    ).filter(
      (f) =>
        !options.settings.filters?.textFieldFilterFields ||
        options.settings.filters.textFieldFilterFields.indexOf(f.name) >= 0,
    );

    const hasOnlyThesePks = this._onlyThesePks.length > 0;
    const onlyThesePksSet = new Set(this._onlyThesePks);
    const filterTextsNumber = filterTexts.length;

    const effectiveData: T[] = [];

    for (let r = 0; r < data.length; r++) {
      const dta = data[r];

      if (hasOnlyThesePks) {
        const pk = pkFunction(dta)?.toString();
        if (!pk || !onlyThesePksSet.has(pk)) continue;
      }

      // Apply FilterTree (column filters)
      if (!this._filterTree.passesFilter(dta)) {
        continue;
      }

      // Apply text search
      if (filterTextsNumber > 0) {
        let matchesAllTerms = true;

        for (let j = 0; j < filterTextsNumber; j++) {
          const term = filterTexts[j];
          let termMatched = false;

          for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const dataVal = String(
              field.sortValueFunction
                ? field.sortValueFunction(dta)
                : field.valueFunction
                ? field.valueFunction(dta)
                : dta[field.name],
            ).toUpperCase();

            if (dataVal.includes(term)) {
              termMatched = true;
              break;
            }
          }

          if (!termMatched) {
            matchesAllTerms = false;
            break;
          }
        }

        if (!matchesAllTerms) {
          continue;
        }
      }

      effectiveData.push(dta);
    }

    this.effectiveData = effectiveData;

    if (this.options.onFilterTextChange) {
      this.options.onFilterTextChange(this.filterText, this._filterTree);
    }

    this.options.settings.userOptionsStorage?.storeFilterText(this.filterText);
    this.options.settings.userOptionsStorage?.storeFilterTree(
      this._filterTree.toJSON(),
      undefined,
      undefined,
      undefined,
    );

    return true;
  }

  /**
   * Hide and show rows, depending on filterText
   */
  public applySearch(searchTerm: string) {
    const dataChanged =
      this.updateEffectiveDataWithSearchAndFilters(searchTerm);

    if (!dataChanged) return;

    // 1. Counter
    this.updateSearchCounter();

    // 2. Table Body
    const initialRows = this.options.initialRows || DEFAULT_ROWS_VISIBLE;
    removeChildren(this.nodes.tbody);
    this.renderGroupFromToIndex(0, initialRows, this.nodes.tbody, false);
    this.setParentHeight();
  }

  public updateSearchCounter() {
    if (!this._searchResults) return;

    const filteredCount = this.effectiveData.length,
      dataCount = this.sortedData.length;

    if (this.filterText) {
      this._searchResults.innerHTML = `${SmartTable.numberFormat.format(
        filteredCount,
      )} <small>${SmartTable.numberFormat.format(dataCount)}</small>`;
      this._searchResults.style.display = "inline-block";
    } else {
      this._searchResults.style.display = "none";
    }
  }

  private updateSingleData(pk: string, dta: T, refresh = false): number {
    const dtaIdx = this.options.data.findIndex(
      (d) => this.options.pkFunction(d) === pk,
    );

    if (dtaIdx < 0) return dtaIdx;

    const newData: T = { ...this.options.data[dtaIdx], ...dta };

    this.options.data[dtaIdx] = newData;

    if (refresh) this.updateData();

    return dtaIdx;
  }

  /**
   * Updates the table with the new data. Re-constructs all the HTML.
   * @param data An array of data
   * @param useInternalSort boolean. If false, it won't sort internally
   */
  public updateData(data?: Array<T>, useInternalSort = true) {
    let dataToUse: Array<T>;
    if (data === undefined) {
      dataToUse = this.options.data;
    } else {
      this.options.data = data;
      dataToUse = data;
    }

    if (useInternalSort && this.sortedBy) {
      this.sortedData = dataToUse.slice().sort(sortByFields(this.sortedBy));
      this.options.settings?.userOptionsStorage?.storeSortFields(this.sortedBy);
    } else {
      this.sortedData = dataToUse;
    }

    this._buildHtml(this.options, true);
  }

  /**
   * Update caption of table
   * @param title
   */
  public updateTitle(title: string) {
    this.options.title = title;
    return this;
  }

  /**
   * Clear the filter input box and update visibility
   */
  public clearSearch = () => {
    if (this._searchInput) {
      this._searchInput.value = "";
      this._searchInput.dispatchEvent(new Event("keyup"));
    }

    if (this.options.onFilterTextChange) {
      this.options.onFilterTextChange("", undefined);
    }

    return this._searchInput;
  };

  public selectRows({
    index,
    pk,
    pks,
    startIndex,
    endIndex,
    clearSelection,
    scrollIntoView = false,
  }: TSelectRowProps): Array<number> {
    const rows = this.rows,
      rowsLenght = rows.length;

    let sIndex: number = startIndex || -1;
    let eIndex: number = endIndex || -1;

    // Flip if draggin upwards
    if (sIndex >= 0 && eIndex >= 0 && eIndex < sIndex) {
      sIndex = eIndex;
      eIndex = startIndex || -1;
    }

    if (index !== undefined && index >= 0 && index < rowsLenght) {
      // By index
      sIndex = index;
      eIndex = sIndex;
    } else if (pk !== undefined) {
      // By PK
      sIndex = this.rowsByPk[pk] !== undefined ? this.rowsByPk[pk].index : -1;
      eIndex = sIndex;
    }

    // Select
    if (sIndex >= 0 && sIndex <= eIndex && eIndex < rowsLenght) {
      this.selection = rows.filter(filterSelected);
    } else if (pks?.length) {
      // By PKs
      const pksSet = new Set(pks);
      this.selection = rows.filter((r) => pksSet.has(r.pk) && r.visible);
    } else if (clearSelection) {
      // Clear
      this.selection = [];
    }

    // Apply selected classes
    rows.forEach((r) => {
      if (!clearSelection && this.selection.includes(r)) {
        if (!r.row.dataset["rowSelected"])
          r.row.dataset["rowSelected"] = "true";
      } else {
        delete r.row.dataset["rowSelected"];
      }
    });

    if (scrollIntoView && this.selection.length) {
      const effectiveData = this.effectiveData;
      const effectiveDataLength = effectiveData.length;
      const firstRowSelected = this.selection[0];
      const block =
        typeof scrollIntoView === "boolean"
          ? "end"
          : scrollIntoView === "optimal"
          ? firstRowSelected.index < 9
            ? "nearest"
            : firstRowSelected.index > effectiveDataLength - 2
            ? "end"
            : "nearest"
          : scrollIntoView;

      firstRowSelected.row.scrollIntoView({
        behavior: "smooth",
        block,
      });
    }

    return this.selection.map((r) => r.index);

    function filterSelected(r: IRow<T>, idx: number) {
      return r.visible && idx >= sIndex && idx <= eIndex;
    }
  }

  public getRowByPk(pk: string): IRow<T> | undefined {
    return this.rowsByPk[pk];
  }

  public removeRow({
    index,
    pk,
  }: { index: number; pk?: never } | { index?: never; pk: string }): boolean {
    const rows = this.rows,
      rowsByPk = this.rowsByPk,
      rowsLenght = rows.length;

    if (index !== undefined && index >= 0 && index < rowsLenght) {
      const rowFoundByIndex = rows[index];
      if (rowFoundByIndex !== undefined) {
        rows.splice(index, 1);
        delete rowsByPk[rowFoundByIndex.pk];

        reIndexRows();

        const parentNode = rowFoundByIndex.row.parentNode;
        if (parentNode) parentNode.removeChild(rowFoundByIndex.row);
      }

      return true;
    } else if (pk) {
      const indexFound = rows.findIndex((r) => r.pk === pk);
      const rowFound = indexFound >= 0 ? rows[indexFound] : undefined;
      if (rowFound) {
        rows.splice(indexFound, 1);
        delete rowsByPk[rowFound.pk];

        reIndexRows();

        const parentNode = rowFound.row.parentNode;
        if (parentNode) parentNode.removeChild(rowFound.row);

        return true;
      }
    }

    return false;

    function reIndexRows() {
      rows.forEach((row, index) => {
        row.index = index;
      });
    }
  }

  public appendToHeaderPseudoSlot(node: HTMLElement, removePrevious = true) {
    if (removePrevious && this._headerPseudoSlotContent) {
      this._headerPseudoSlotContent.remove();
    }

    this._headerPseudoSlotContent = node;

    if (this._headerPseudoSlot) this._headerPseudoSlot.appendChild(node);
  }

  public updateRowWithNewData(pk: string, dta: T) {
    const dtaIdx = this.updateSingleData(pk, dta);

    if (dtaIdx < 0) return;

    const newData: T = this.options.data[dtaIdx];

    const row = this.rowsByPk[pk];

    if (!row) return;

    row.data = newData;

    const tr = this.renderRow(
      newData,
      row.index,
      true,
      this.options.settings.rowAddCheckboxes || false,
    );

    if (tr) {
      row.row.replaceWith(tr);
      row.row = tr;
    }
  }

  get filterHasConditions() {
    return this._filterTree.isEmptyFilter();
  }
}

customElements.define("smart-table", SmartTable);

declare global {
  interface HTMLElementTagNameMap {
    "smart-table": SmartTable<unknown>;
  }
}

function renderFieldCell<T>(
  dta: T,
  pk: string,
  smartTableRef: SmartTable<T>,
  tr: HTMLTableRowElement,
  {
    name,
    className: classNamePrev,
    valueFunction,
    mapper,
    type,
    icon,
    iconVariant,
    onClick,
    options,
    label,
    smallPadding,
    spanColumns,
    showCellLoading,
    fixedWidth,
    ...rest
  }: ITableField<T>,
) {
  const className = classNamePrev
    ? typeof classNamePrev === "function"
      ? classNamePrev(dta)
      : classNamePrev
    : "";

  const td = document.createElement("td");
  td.className = `td-smt-${name as string} ${className || ""}`;

  const colSpan = spanColumns ? spanColumns(dta) : 1;
  if (colSpan !== undefined && colSpan !== 1) {
    if (colSpan === 0) {
      return;
    }
    td.colSpan = colSpan;
  }

  if (showCellLoading) {
    const loadingEl = document.createElement("sl-spinner");
    loadingEl.slot = "prefix";
    loadingEl.style.display = "inline-block";
    loadingEl.style.marginRight = "5px";
    td.appendChild(loadingEl);
    tr.appendChild(td);
    return;
  }

  const value = valueFunction ? valueFunction(dta) : String(dta[name]);

  switch (type) {
    case "icon":
      {
        const iconStr = typeof icon === "function" ? icon(dta) : icon;
        if (iconStr !== "") {
          const iconBtn = document.createElement("sl-button");
          iconBtn.size = "small";
          const variant =
            typeof iconVariant === "function"
              ? iconVariant(dta)
              : iconVariant || "primary";
          iconBtn.variant = variant;
          iconBtn.outline = true;
          const iconEl = document.createElement("sl-icon") as SlIcon;
          iconEl.name = iconStr;
          iconBtn.appendChild(iconEl);

          const isDisabled = (rest as IDisabledFn<T>)?.disabled?.(dta) || false;

          if (isDisabled) {
            iconBtn.disabled = true;
          } else {
            iconBtn.onclick = () => {
              onClick(dta, name, smartTableRef, iconBtn);
            };
          }

          td.classList.add("centered");
          td.classList.add("v-align-middle");
          td.appendChild(iconBtn);
        }
      }
      break;

    case "actions":
      {
        const opts = typeof options === "function" ? options(dta) : options;

        const changeStateSelect = createSelectShoelace({
          id: "actions",
          caret: false,
          pill: true,
          ommitCheckSign: true,
          size: "small",
          variant:
            typeof iconVariant === "function"
              ? iconVariant(dta)
              : iconVariant || "neutral",
          buttonText: "",
          iconPrefix: typeof icon === "function" ? icon(dta) : icon,
          disabled: opts.length === 0,
          title: label,
          options: opts.map((o) => ({
            name: o.label,
            value: o.value,
            icon: o.icon,
            variant: o.variant,
          })),
          onChange: (d) => onClick(dta, name, smartTableRef, d),
        });

        td.className = "centered v-align-middle";
        td.appendChild(changeStateSelect);
      }
      break;

    case "inlineToggle":
      {
        const inlineToggle = document.createElement("sl-switch") as SlSwitch;
        inlineToggle.id = `toggle-${pk}-${String(name)}`;
        inlineToggle.name = String(name);

        inlineToggle.checked = (rest as ITableFieldInlineToggle<T>).getValueFn(
          dta,
        );

        inlineToggle.addEventListener("sl-change", (ev) => {
          const target = ev.target as HTMLInputElement;
          (rest as ITableFieldInlineToggle<T>).onToggle(
            dta,
            name,
            smartTableRef,
            target.checked,
          );
        });

        td.appendChild(inlineToggle);
      }
      break;

    case "inlineRadioButtons":
      {
        const inlineRbns = document.createElement("tf-radio-group");
        inlineRbns.id = `tfrb-${pk}-${String(name)}`;
        inlineRbns.name = `tfrb-${pk}-${String(name)}`;
        inlineRbns.items = (
          rest as ITableFieldInlineRadioButtons<T>
        ).radioOptions;
        inlineRbns.style.display = "inline-block";

        inlineRbns.value = valueFunction
          ? valueFunction(dta)
          : String(dta[name]) || "";

        inlineRbns.addEventListener("sl-change", (ev) => {
          const target = ev.target as HTMLInputElement;
          (rest as ITableFieldInlineRadioButtons<T>).onChange(
            dta,
            name,
            smartTableRef,
            target.value,
            target,
          );
        });

        td.appendChild(inlineRbns);
      }
      break;

    case "inlineEdit":
      {
        const inlineInput = document.createElement("sl-input");
        inlineInput.id = `input-${pk}-${String(name)}`;
        inlineInput.name = String(name);

        inlineInput.size = "small";
        const editType = (rest as ITableFieldInlineEdit<T>).editType || "text";
        inlineInput.type = editType;
        inlineInput.style.width = fixedWidth || "100%";

        inlineInput.value = valueFunction
          ? valueFunction(dta)
          : String(dta[name]) || "";
        inlineInput.addEventListener("sl-change", (ev) => {
          const target = ev.target as HTMLInputElement;
          (rest as ITableFieldInlineEdit<T>).onChange(
            dta,
            name,
            smartTableRef,
            editType === "text" ? target.value : target.valueAsNumber,
            target,
          );
        });

        td.appendChild(inlineInput);
      }
      break;

    case "inlineEditPadded":
      {
        const inlineInput = document.createElement("tf-input-padded");
        inlineInput.id = `input-${pk}-${String(name)}`;
        inlineInput.name = String(name);

        inlineInput.size = "small";
        inlineInput.padZeros = (
          rest as ITableFieldInlineEditPadded<T>
        ).padZeros;
        inlineInput.style.width = fixedWidth || "100%";

        inlineInput.value = Number(dta[name]);
        inlineInput.addEventListener("sl-change", (ev) => {
          const target = ev.target as HTMLInputElement;
          (rest as ITableFieldInlineEditPadded<T>).onChange(
            dta,
            name,
            smartTableRef,
            Number((target as HTMLInputElement).value),
            target,
          );
        });

        td.appendChild(inlineInput);
      }
      break;

    case "inlineEditWithUnits":
      {
        const inlineInput = document.createElement("tf-input-units");
        inlineInput.id = `input-${pk}-${String(name)}`;
        inlineInput.name = String(name);

        inlineInput.size = "small";
        inlineInput.style.width = fixedWidth || "100%";

        const restD = rest as ITableFieldInlineEditWithUnits<T>;

        if (restD.converter) inlineInput.converter = restD.converter;

        if (restD.transponser) inlineInput.transponser = restD.transponser;

        if (restD.noSpinButtons)
          inlineInput.noSpinButtons = restD.noSpinButtons;

        inlineInput.value = dta[name] as number;
        inlineInput.addEventListener("sl-change", (ev) => {
          const target = ev.target as HTMLInputElement;
          (rest as ITableFieldInlineEditWithUnits<T>).onChange(
            dta,
            name,
            smartTableRef,
            target.value,
            target,
          );
        });

        td.appendChild(inlineInput);
      }
      break;

    default:
      if (mapper) {
        const res = mapper(dta);
        if (typeof res === "string") {
          td.innerHTML = res;
        } else {
          td.appendChild(res);
        }
      } else {
        td.innerHTML = value;
      }
  }

  if (smallPadding) td.classList.add("small-padding");

  tr.appendChild(td);
}

function createTdWithCheckboxSelector(
  pk: string,
  tr: HTMLTableRowElement,
  refs: {
    pksSelected: Set<string>;
    globalCheckbox: SlCheckbox | undefined;
    globalSelectedActionButton: SelectShoelace | undefined;
    rowsCheckboxes: SlCheckbox[];
  },
  cb: (pksSelected: string[]) => void,
) {
  const td = document.createElement("td");
  td.className = "td-smt-selector";
  const chk = document.createElement("sl-checkbox") as SlCheckbox;
  chk.value = pk;

  if (refs.pksSelected.has(pk)) chk.checked = true;

  refs.rowsCheckboxes.push(chk);

  chk.addEventListener("sl-change", (ev) => {
    const target = ev.target as SlCheckbox;

    if (target.checked) {
      refs.pksSelected.add(pk);
    } else {
      refs.pksSelected.delete(pk);
    }

    if (refs.globalCheckbox) {
      refs.globalCheckbox.indeterminate = true;
    }

    if (refs.globalSelectedActionButton) {
      refs.globalSelectedActionButton.disabled = refs.pksSelected.size === 0;
    }

    cb(Array.from(refs.pksSelected));
  });

  td.appendChild(chk);
  tr.appendChild(td);
}
